import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useDaggerheartData } from '../context/DataContext';

const BerrysRestaurantManager = ({ character, updateCharacter, onManagerClose }) => {
  const [selectedOrders, setSelectedOrders] = useState({});
  const [totalCost, setTotalCost] = useState(0);
  const { campaignFrames } = useDaggerheartData();

  // Get Berry's Restaurant data from campaign frame
  const restaurantData = campaignFrames['beast-feast'].customMechanics.berrysRestaurant;
  const maxSpendPerPC = restaurantData.maxSpendPerPC;

  // Helper function to calculate total gold in handfuls
  const calculateTotalGold = (goldData) => {
    if (!goldData || typeof goldData === 'number') {
      return goldData || 0;
    }
    const { handfuls = 0, bags = 0, chests = 0 } = goldData;
    return handfuls + (bags * 10) + (chests * 100);
  };

  // Helper function to format gold display
  const formatGoldDisplay = (goldData) => {
    if (!goldData || typeof goldData === 'number') {
      return goldData || 0;
    }
    const { handfuls = 0, bags = 0, chests = 0 } = goldData;
    if (chests > 0) {
      return `${chests}📦 ${bags}💼 ${handfuls}💰`;
    } else if (bags > 0) {
      return `${bags}💼 ${handfuls}💰`;
    } else {
      return `${handfuls}💰`;
    }
  };

  // Helper function to spend gold and return new gold structure
  const spendGold = (goldData, amount) => {
    if (!goldData || typeof goldData === 'number') {
      return Math.max((goldData || 0) - amount, 0);
    }
    
    let { handfuls = 0, bags = 0, chests = 0 } = goldData;
    let remainingToSpend = amount;
    
    // Spend from handfuls first
    if (remainingToSpend > 0 && handfuls > 0) {
      const spendFromHandfuls = Math.min(handfuls, remainingToSpend);
      handfuls -= spendFromHandfuls;
      remainingToSpend -= spendFromHandfuls;
    }
    
    // Convert bags to handfuls if needed
    while (remainingToSpend > 0 && bags > 0) {
      bags -= 1;
      handfuls += 10;
      const spendFromHandfuls = Math.min(handfuls, remainingToSpend);
      handfuls -= spendFromHandfuls;
      remainingToSpend -= spendFromHandfuls;
    }
    
    // Convert chests to bags/handfuls if needed
    while (remainingToSpend > 0 && chests > 0) {
      chests -= 1;
      bags += 10;
      // Convert bags to handfuls as needed
      while (remainingToSpend > 0 && bags > 0) {
        bags -= 1;
        handfuls += 10;
        const spendFromHandfuls = Math.min(handfuls, remainingToSpend);
        handfuls -= spendFromHandfuls;
        remainingToSpend -= spendFromHandfuls;
      }
    }
    
    return { handfuls, bags, chests };
  };
  const goldCost = restaurantData.goldCost;

  // Available downtime moves
  const downtimeMoves = [
    {
      id: 'heal',
      name: 'Heal Hit Points',
      emoji: '❤️',
      description: 'Recover HP equal to your meal rating',
      cost: goldCost
    },
    {
      id: 'stress',
      name: 'Clear Stress', 
      emoji: '😌',
      description: 'Clear Stress equal to your meal rating',
      cost: goldCost
    },
    {
      id: 'hope',
      name: 'Gain Hope',
      emoji: '✨', 
      description: 'Gain Hope equal to your meal rating (max 6)',
      cost: goldCost
    }
  ];

  // Handle order selection
  const handleOrderChange = (moveId, quantity) => {
    const newOrders = { ...selectedOrders };
    if (quantity === 0) {
      delete newOrders[moveId];
    } else {
      newOrders[moveId] = Math.min(quantity, maxSpendPerPC);
    }
    
    setSelectedOrders(newOrders);
    
    // Calculate total cost
    const newTotalCost = Object.values(newOrders).reduce((sum, qty) => sum + (qty * goldCost), 0);
    setTotalCost(newTotalCost);
  };

  // Handle ordering
  const handleOrder = () => {
    const currentGold = calculateTotalGold(character.gold);
    
    if (totalCost > currentGold) {
      toast.success(`Not enough gold! You need ${totalCost} handfuls of gold but only have ${currentGold}.`);
      return;
    }

    if (totalCost === 0) {
      toast.success('Please select at least one item to order.');
      return;
    }

    // Apply the benefits
    let updates = { gold: spendGold(character.gold, totalCost) };
    
    Object.entries(selectedOrders).forEach(([moveId, quantity]) => {
      const totalBenefit = quantity; // Each order gives 1 point of benefit
      
      switch (moveId) {
        case 'heal':
          const newHp = Math.min(character.hp + totalBenefit, character.maxHp);
          updates.hp = newHp;
          break;
        case 'stress':
          const newStress = Math.max(character.stress - totalBenefit, 0);
          updates.stress = newStress;
          break;
        case 'hope':
          const newHope = Math.min(character.hope + totalBenefit, 6);
          updates.hope = newHope;
          break;
      }
    });

    updateCharacter(updates);
    
    // Show success message
    const orderSummary = Object.entries(selectedOrders)
      .map(([moveId, qty]) => {
        const move = downtimeMoves.find(m => m.id === moveId);
        return `${qty}x ${move.name}`;
      })
      .join(', ');
    
    toast.success(`Order placed! ${orderSummary} for ${totalCost} handfuls of gold.`);
    
    // Reset form
    setSelectedOrders({});
    setTotalCost(0);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-purple-600 mb-2">🏪 Berry's Restaurant</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Order delicious meals during downtime instead of cooking
        </p>
      </div>

      {/* Current Resources */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <h4 className="font-semibold mb-2">Your Resources</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>💰 Gold: <span className="font-medium">{formatGoldDisplay(character.gold)}</span></div>
          <div>❤️ HP: <span className="font-medium">{character.hp}/{character.maxHp}</span></div>
          <div>😰 Stress: <span className="font-medium">{character.stress || 0}</span></div>
          <div>✨ Hope: <span className="font-medium">{character.hope || 0}/6</span></div>
        </div>
      </div>

      {/* Menu */}
      <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
        <h4 className="font-semibold mb-3 text-purple-800 dark:text-purple-200">📋 Menu</h4>
        <p className="text-sm text-purple-700 dark:text-purple-300 mb-4">
          Each meal costs {goldCost} handful of gold and provides 1 point of the selected benefit. Maximum {maxSpendPerPC} orders per character.
        </p>
        
        <div className="space-y-3">
          {downtimeMoves.map(move => (
            <div key={move.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-md border">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{move.emoji}</span>
                  <span className="font-medium">{move.name}</span>
                  <span className="text-sm text-gray-500">({goldCost} handful of gold each)</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{move.description}</p>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleOrderChange(move.id, (selectedOrders[move.id] || 0) - 1)}
                  disabled={(selectedOrders[move.id] || 0) <= 0}
                  className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-400 flex items-center justify-center text-sm font-bold"
                >
                  −
                </button>
                <span className="w-8 text-center font-medium">{selectedOrders[move.id] || 0}</span>
                <button
                  onClick={() => handleOrderChange(move.id, (selectedOrders[move.id] || 0) + 1)}
                  disabled={(selectedOrders[move.id] || 0) >= maxSpendPerPC}
                  className="w-8 h-8 rounded-full bg-purple-200 dark:bg-purple-600 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-400 flex items-center justify-center text-sm font-bold"
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Order Summary */}
      {totalCost > 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
          <h4 className="font-semibold mb-2 text-green-800 dark:text-green-200">🧾 Order Summary</h4>
          <div className="space-y-1 text-sm">
            {Object.entries(selectedOrders).map(([moveId, quantity]) => {
              const move = downtimeMoves.find(m => m.id === moveId);
              return (
                <div key={moveId} className="flex justify-between">
                  <span>{quantity}x {move.name}</span>
                  <span>{quantity * goldCost} handfuls of gold</span>
                </div>
              );
            })}
            <div className="border-t pt-1 flex justify-between font-bold">
              <span>Total:</span>
              <span>{totalCost} handfuls of gold</span>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          onClick={handleOrder}
          disabled={totalCost === 0 || totalCost > calculateTotalGold(character.gold)}
          className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
        >
          Place Order ({totalCost} handfuls of gold)
        </button>
        <button
          onClick={onManagerClose}
          className="px-4 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600 font-medium"
        >
          Leave Restaurant
        </button>
      </div>
    </div>
  );
};

export default BerrysRestaurantManager;
